import { useEffect, useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, Icon, theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { ErrorBox } from '~/components/ErrorBox';
import { Select } from '~/components/Select';
import { AccordionBase } from '~/components/Accordion';
import { BehaviorChartView, CONTROLS_HEIGHT } from '~/components/BehaviorChartView';
import { useStudentBehaviorReportQuery, useStudentSummaryMutation, type Timeframe } from '~/services/studentData';
import { studentDataStore } from '~/store/studentData/studentDataStore';
import { useActiveStudentContext } from './useActiveStudentContext';

import { TIMEFRAME_OPTIONS, getTimeframeLabel } from './sharedFilters';

export function OverviewTab() {
  const { student, studentId, periodId, periodCreatedAt } = useActiveStudentContext();
  const timeframe = studentDataStore((s) => s.timeframe);
  const setTimeframe = studentDataStore((s) => s.setTimeframe);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);

  const {
    data: chartData,
    status: chartStatus,
    isFetching: chartFetching,
  } = useStudentBehaviorReportQuery({
    studentId,
    periodId,
    periodCreatedAt,
    timeframe,
  });

  const { mutate: generateSummary, data: summaryData, status: summaryStatus } = useStudentSummaryMutation();

  useEffect(() => {
    if (!studentId) return;
    generateSummary({
      studentId,
      periodId,
      periodCreatedAt,
      timeframe,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, timeframe]);

  const timeframeLabel = getTimeframeLabel(timeframe, { case: 'sentence' });

  const summaryContent = (
    <View style={styles.summaryContent}>
      {summaryStatus === 'pending' && <Loading backgroundColor="transparent" />}
      {summaryStatus === 'error' && <ErrorBox text="Failed to load summary. Please close this window and try again." />}
      {(summaryStatus === 'success' || summaryStatus === 'idle') && summaryData && (
        <Text style={styles.summaryText}>{summaryData.summary}</Text>
      )}
    </View>
  );

  const expandButton = (
    <Pressable onPress={() => setSummaryCollapsed(false)}>
      <Icon type="downChevron" width={16} height={10} />
    </Pressable>
  );

  return (
    <View style={styles.container}>
      <BehaviorChartView
        data={chartData}
        loading={chartStatus === 'pending' || chartFetching}
        hasError={chartStatus === 'error'}
        title={`${student?.voiceName}'s behavior (${timeframeLabel})`}
        errorMessage="Failed to load chart. Please close this window and try again."
        controls={
          <Select
            value={timeframe}
            onChange={(v) => setTimeframe(v as Timeframe)}
            options={TIMEFRAME_OPTIONS}
            height={CONTROLS_HEIGHT}
          />
        }
      />
      <AccordionBase
        title={`${student?.voiceName}'s summary (${timeframeLabel})`}
        icon={<Icon type="document" />}
        content={summaryContent}
        rightControls={expandButton}
        isCollapsed={summaryCollapsed}
        onToggleCollapse={setSummaryCollapsed}
        noContentBorder={true}
        containerStyle={styles.summaryContainer}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.xlarge * 2,
    paddingBottom: theme.spacing.xxlarge,
  },
  summaryContainer: {
    backgroundColor: theme.colors.lightGray,
    borderWidth: 0,
  },
  summaryContent: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    padding: theme.spacing.large,
  },
  summaryText: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
  },
});
